/**
 * Firm-message schemas — Portal Messages threads as CC consumes them (#542).
 *
 * The store is the portal's `Portal Messages` Airtable table, owned entirely
 * by `portal/worker.js`. CC never reads or writes the table itself (ADR-0071:
 * firm-attributed feeds are portal-only) — the CC worker PROXIES the portal's
 * LF-admin message doors server-to-server (`/api/admin/messages*`, the
 * ops-secret proxy pattern in routes/shared.ts), and these schemas pin the door's wire shape so the
 * web app gets a validated contract.
 *
 * Thread model (portal-defined): a root message + flat replies, one level
 * deep. `case_id` is the root's `Case` link — a PLACEMENT record id, present
 * only on threads started through a per-case door; firm-level threads carry
 * null. `unread` is thread-level "any Firm-authored message not yet Read by
 * LF" — the same flag the portal's own admin inbox reads, so marking read in
 * CC clears it there too (intended: CC is the working surface).
 *
 * Renderer guard (ADR-0071 amendment, #542): authorship renders from the
 * STRUCTURED `author_role` / `author_display_name` fields only — never parsed
 * out of `body` — and `body` renders as auto-escaped text, never HTML.
 */

import { z } from "zod";

/** One message in a thread, exactly as the portal door projects it. */
export const FirmMessageSchema = z.object({
  /** Airtable record ID. */
  id: z.string(),
  /** Message text. Rendered as plain text (renderer guard — never HTML). */
  body: z.string(),
  /** "Firm User" | "LF" in practice; kept open — the door doesn't enum it. */
  author_role: z.string(),
  /** Display name, e.g. "Dana @ Windsor Troy" / "Chris @ LegalFlare". */
  author_display_name: z.string(),
  author_email: z.string(),
  /** ISO-8601 datetime. */
  created_at: z.string(),
});
export type FirmMessage = z.infer<typeof FirmMessageSchema>;

/**
 * One thread from `GET /api/admin/messages`. `firm_id` is nullish-normalised:
 * the portal omits the key when the root has no Firm link (JSON drops
 * undefined), so absent and null both project to null.
 */
export const FirmThreadSchema = z.object({
  /** Thread-root Airtable record ID — the id reply/mark-read verbs take. */
  id: z.string(),
  subject: z.string(),
  /** "Per-case" for case-door threads; "" for user/general threads. */
  channel: z.string(),
  /** True when the thread root was LF-authored. */
  lf_initiated: z.boolean(),
  /** "Firm" (firm-wide) | "Direct" (one contact + author only). */
  visibility: z.string(),
  firm_id: z.string().nullish().transform((v) => v ?? null),
  firm_name: z.string(),
  /** PLACEMENT record id the thread is tagged to, or null (firm-level). */
  case_id: z.string().nullable(),
  /** Denormalised label, e.g. "Smith (Norinsberg → CityLaw)". "" when untagged. */
  case_label: z.string(),
  /** Root first, then replies ascending by created_at. */
  messages: z.array(FirmMessageSchema),
  /** ISO-8601 datetime of the newest message. */
  last_activity: z.string(),
  /** Any Firm-authored message in the thread not yet Read by LF. */
  unread: z.boolean(),
});
export type FirmThread = z.infer<typeof FirmThreadSchema>;

// ---------------------------------------------------------------------------
// API contracts — /api/operations/messages (CC worker proxy routes).
// ---------------------------------------------------------------------------

/** GET /api/operations/messages/case/:leadId — that matter's threads. */
export const CaseThreadsResponseSchema = z.object({
  threads: z.array(FirmThreadSchema),
});
export type CaseThreadsResponse = z.infer<typeof CaseThreadsResponseSchema>;

/** POST /api/operations/messages/:threadId/reply */
export const ReplyMessageRequestSchema = z.object({
  body: z.string().min(1),
});
export type ReplyMessageRequest = z.infer<typeof ReplyMessageRequestSchema>;

export const ReplyMessageResponseSchema = z.object({
  /** Airtable record id of the persisted reply. */
  message_id: z.string(),
});
export type ReplyMessageResponse = z.infer<typeof ReplyMessageResponseSchema>;

/** PATCH /api/operations/messages/:threadId/read */
export const MarkThreadReadResponseSchema = z.object({
  /** How many messages flipped Read by LF (0 = already read; idempotent). */
  updated: z.number(),
});
export type MarkThreadReadResponse = z.infer<typeof MarkThreadReadResponseSchema>;

// ---------------------------------------------------------------------------
// Slice 2 — the global Messages view (#542 decision 4).
// ---------------------------------------------------------------------------

/**
 * GET /api/operations/messages — every thread, partitioned into the two honest
 * lanes (#542 decision 2): case-tagged threads (root `Case` link present) and
 * firm-level threads (untagged — no backfill guessing). Each lane is sorted
 * unread-first, then newest activity first, by the CC worker — the client
 * renders in order.
 */
export const GlobalThreadsResponseSchema = z.object({
  caseTagged: z.array(FirmThreadSchema),
  firmLevel: z.array(FirmThreadSchema),
});
export type GlobalThreadsResponse = z.infer<typeof GlobalThreadsResponseSchema>;

/**
 * Portal-enforced compose limits (portal/worker.js SUBJECT_MAX / MSG_BODY_MAX).
 * Mirrored here so CC rejects before the proxy hop instead of surfacing the
 * portal's 400 copy.
 */
export const MESSAGE_SUBJECT_MAX = 200;
export const MESSAGE_BODY_MAX = 5000;

/**
 * POST /api/operations/messages/case — start a case-tagged thread. The portal
 * door (`/api/admin/message/case`) keys on a PLACEMENT id + which side of the
 * matter to address (OF/HF); recipients default to the firm's lead-recipient
 * contacts (`recipients_mode: "default"` — recipient preview is a named
 * follow-up, not built).
 */
export const ComposeCaseMessageRequestSchema = z.object({
  placementId: z.string().min(1),
  side: z.enum(["OF", "HF"]),
  subject: z.string().min(1).max(MESSAGE_SUBJECT_MAX),
  body: z.string().min(1).max(MESSAGE_BODY_MAX),
});
export type ComposeCaseMessageRequest = z.infer<typeof ComposeCaseMessageRequestSchema>;

export const ComposeCaseMessageResponseSchema = z.object({
  /** Airtable record id of the new thread root. */
  message_id: z.string(),
  /** How many firm contacts were emailed. */
  recipients: z.number(),
});
export type ComposeCaseMessageResponse = z.infer<typeof ComposeCaseMessageResponseSchema>;

/**
 * POST /api/operations/messages/firm — start a firm-level thread. The portal
 * door (`/api/admin/message/user`) addresses ONE contact (`contact_id`), not a
 * firm: visibility "Firm" shows the thread to every contact at the firm,
 * "Direct" only to the recipient + LF. Verified at build (#542 plan: "verify
 * exact payload").
 */
export const ComposeFirmMessageRequestSchema = z.object({
  contactId: z.string().min(1),
  visibility: z.enum(["Firm", "Direct"]).default("Firm"),
  subject: z.string().min(1).max(MESSAGE_SUBJECT_MAX),
  body: z.string().min(1).max(MESSAGE_BODY_MAX),
});
export type ComposeFirmMessageRequest = z.infer<typeof ComposeFirmMessageRequestSchema>;

export const ComposeFirmMessageResponseSchema = z.object({
  /** Airtable record id of the new thread root. */
  message_id: z.string(),
});
export type ComposeFirmMessageResponse = z.infer<typeof ComposeFirmMessageResponseSchema>;

/**
 * GET /api/operations/messages/case/:leadId/targets — the compose case
 * picker's second hop. The global search yields a LEAD id; the portal compose
 * door needs a PLACEMENT id + side. This read resolves the lead's placements
 * (the routing trail) so the operator picks the leg — by HF firm name — when
 * more than one exists. OF-side sends resolve the OF via the lead on the
 * portal side, so any placement id addresses the same OF.
 */
export const CaseComposeTargetSchema = z.object({
  placementId: z.string(),
  /** The leg's Handling Firm name, or null on a broken link. */
  hfFirmName: z.string().nullable(),
  /** The leg's Placement Decision (routing context for the picker), or null. */
  decision: z.string().nullable(),
});
export type CaseComposeTarget = z.infer<typeof CaseComposeTargetSchema>;

export const CaseComposeTargetsResponseSchema = z.object({
  leadRecordId: z.string(),
  clientName: z.string(),
  originatingFirmName: z.string().nullable(),
  /** Newest-sent first — the live leg is almost always the first pick. */
  targets: z.array(CaseComposeTargetSchema),
});
export type CaseComposeTargetsResponse = z.infer<typeof CaseComposeTargetsResponseSchema>;
